import {Button, Select, Typography} from "@bolteu/clay-react";
import {Globe} from "@bolteu/kalep-react-icons";
import {useSelector} from "react-redux";

import {useAppDispatch} from "webform/store/hooks";
import {useI18n} from "i18n/i18n";
import {getLocaleArray} from "i18n/translations";
import {resolveLocale} from "i18n/LookupTranslations";
import {setLocale} from "webform/slices/configSlice";
import {localeSelector} from "webform/store/selectors";

import * as Styled from "./pageTitle.styled";

// The autonyms in the locale map follow each language's own casing (e.g. "français",
// "eesti" are lowercase natively), so capitalise the first letter for a consistent list.
const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

// Page header: "Contact us" heading + a Clay Select locale switcher + subtitle.
export const PageTitle = () => {
    const i18n = useI18n();
    const dispatch = useAppDispatch();
    const selectedLocale = useSelector(localeSelector);
    // Show the locale the app actually resolved to (e.g. navigator "en-US" -> "en-gb"),
    // so the trigger displays a real language name instead of the raw detected code.
    const activeLocale = resolveLocale(selectedLocale);
    const locales = getLocaleArray().map((locale) => ({...locale, title: capitalize(locale.title)}));

    return (
        <>
            <Styled.Header>
                <Typography defaultTag="h1" className="bolt-font-heading-s-accent">
                    {i18n("webform.header.title.contact_us")}
                </Typography>
                <Select.Root
                    items={locales.map((locale) => ({value: locale.id, label: locale.title}))}
                    value={activeLocale}
                    onValueChange={(value) => {
                        if (typeof value === "string") {
                            dispatch(setLocale(value));
                        }
                    }}
                >
                    {/* Select.Trigger always applies its own w-full/rounded-md/justify-end
                        regardless of `render` target, so force the pill shape it fights with Button over. */}
                    <Select.Trigger className="!w-fit !rounded-full" render={<Button variant="secondary" size="sm" />}>
                        <Button.Icon icon={<Globe />} />
                        <Button.Label>
                            <Select.Value />
                        </Button.Label>
                    </Select.Trigger>
                    <Select.Portal>
                        <Select.Positioner>
                            <Styled.LocalePopup>
                                <Select.List>
                                    {locales.map((locale) => (
                                        <Select.Item key={locale.id} value={locale.id}>
                                            <Select.ItemText>{locale.title}</Select.ItemText>
                                            <Select.ItemIndicator />
                                        </Select.Item>
                                    ))}
                                </Select.List>
                            </Styled.LocalePopup>
                        </Select.Positioner>
                    </Select.Portal>
                </Select.Root>
            </Styled.Header>
            <Typography className="bolt-font-body-m-regular text-secondary py-5">
                {i18n("webform.header.subtitle.need_more_info")}
            </Typography>
        </>
    );
};
